import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { ApiError } from "@/services/ApiService";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date));
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

const KNOWN_API_ERRORS: Record<string, string> = {
  "Too many attempts. Please try again later.":
    "You've made too many attempts. Please wait a bit and try again.",
  "Invalid verification code":
    "That code doesn't match. Please check and try again.",
  "No verification code found. Please request a new one.":
    "We couldn't find a code for this address. Tap resend to get a new one.",
  "Verification code has expired. Please request a new one.":
    "That code has expired. Tap resend to get a fresh one.",
  "Verification expired or not found. Please request a new code.":
    "That code has expired. Tap resend to get a fresh one.",
  "Invalid email format": "Please enter a valid email address.",
  "Verification failed":
    "Something went wrong verifying your code. Please try again.",
  "Failed to send verification code":
    "We couldn't send the code right now. Please try again in a moment.",
  "Email delivery is temporarily unavailable. Please try again later.":
    "Email delivery is temporarily unavailable. Please try again later.",
  "Too many emails sent. Please wait a moment and try again.":
    "Too many emails sent. Please wait a moment and try again.",
};

const STATUS_FALLBACKS: Record<number, string> = {
  400: "Something didn't look right. Please check your input and try again.",
  401: "That code doesn't match. Please check and try again.",
  404: "We're having trouble reaching our servers. Please try again shortly.",
  408: "The request timed out. Please check your connection and try again.",
  410: "That code has expired. Tap resend to get a fresh one.",
  429: "You've made too many attempts. Please wait a bit and try again.",
  500: "Something went wrong on our end. Please try again in a moment.",
  502: "Our service is temporarily unavailable. Please try again shortly.",
  503: "Our service is temporarily unavailable. Please try again shortly.",
};

export function friendlyError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const known = KNOWN_API_ERRORS[err.message];
    if (known) return known;

    if (err.errorCode) {
      const knownByCode = KNOWN_API_ERRORS[err.errorCode];
      if (knownByCode) return knownByCode;
    }

    const statusFallback = STATUS_FALLBACKS[err.statusCode];
    if (statusFallback) return statusFallback;

    return fallback;
  }

  if (err instanceof TypeError && err.message === "Failed to fetch") {
    return "Unable to connect. Please check your internet connection and try again.";
  }

  return fallback;
}
